import { useSearchParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { useBook } from "@/context/BookContext";
import { usePages, useList } from "@/lib/hooks";
import { PageCanvas } from "@/components/PageCanvas";
import { Spin } from "@/components/common";
import { CoverFront, CoverBack } from "@/components/CoverViews";
import { media } from "@/lib/api";

const L = "ABCDEFGH";

function CoverPreview({ book, side }) {
  const cv = book.cover, H = 400, W = H * (cv.width_cm - cv.spine_cm) / 2 / cv.height_cm, k = 560 / W;
  return (
    <div className="print-page relative shadow-xl overflow-hidden" style={{ width: 560, height: H * k }} data-testid={`preview-cover-${side}`}>
      <div style={{ transform: `scale(${k})`, transformOrigin: "top left", width: W, height: H }}>{side === "front" ? <CoverFront cv={cv} book={book} W={W} H={H} editable={false} /> : <CoverBack cv={cv} W={W} H={H} editable={false} />}</div>
    </div>
  );
}

export default function Preview() {
  const { bookId, book } = useBook();
  const { items: pages } = usePages(bookId);
  const { items: qs } = useList(bookId ? `/books/${bookId}/questions` : null);
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const mode = sp.get("sorular");
  const onlyApproved = sp.get("onayli") === "1";
  if (!book) return <div className="p-10"><Spin /></div>;
  const list = onlyApproved ? pages.filter((p) => p.approved) : pages;
  const approvedQs = qs.filter((q) => q.approved);

  return (
    <div className="flex-1 bg-[#e9ecef] min-h-screen" data-testid="preview-page">
      <div className="no-print sticky top-0 z-20 bg-white border-b px-6 py-3 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="btn-ghost px-3 py-2 flex items-center gap-2"><ArrowLeft size={16} /> Geri</button>
        <div className="font-serif-d text-xl text-navy">{book.title} — {mode === "ogrenci" ? "Öğrenci soruları" : mode === "ogretmen" ? "Öğretmen cevap anahtarı" : "Baskı önizleme"}</div>
        <div className="ml-auto flex gap-2 text-sm">
          <button onClick={() => nav("/onizleme")} className="btn-ghost px-3 py-2" data-testid="preview-book">Kitap</button>
          <button onClick={() => nav("/onizleme?onayli=1")} className="btn-ghost px-3 py-2" data-testid="preview-approved">Yalnız onaylı</button>
          <button onClick={() => nav("/onizleme?sorular=ogrenci")} className="btn-ghost px-3 py-2" data-testid="preview-student">Öğrenci PDF</button>
          <button onClick={() => nav("/onizleme?sorular=ogretmen")} className="btn-ghost px-3 py-2" data-testid="preview-teacher">Öğretmen PDF</button>
          <button onClick={() => window.print()} className="btn-teal px-4 py-2 flex items-center gap-2" data-testid="print-pdf-btn"><Printer size={16} /> PDF olarak kaydet</button>
        </div>
      </div>
      <div className="py-8 space-y-8 flex flex-col items-center">
        {!mode && <>
          <CoverPreview book={book} side="front" />
          {list.map((p) => <PageCanvas key={p.id} page={p} className="print-page w-[560px]" />)}
          {approvedQs.length > 0 && <QuestionPage book={book} qs={approvedQs} teacher={false} />}
          <CoverPreview book={book} side="back" />
        </>}
        {mode && <QuestionPage book={book} qs={approvedQs.length ? approvedQs : qs} teacher={mode === "ogretmen"} />}
      </div>
    </div>
  );
}

function QuestionPage({ book, qs, teacher }) {
  return (
    <div className="print-page w-[560px] bg-white shadow-xl p-10 font-lora" data-testid="questions-print">
      <h2 className="font-serif-d text-2xl text-navy mb-1">{book.title}</h2><div className="text-sm text-[#6b7a8c] mb-6">{teacher ? "Öğretmen — Cevap Anahtarı" : "Okuma Anlama Soruları"}</div>
      {qs.map((q, i) => (
        <div key={q.id} className="mb-5"><div className="font-semibold text-navy">{i + 1}. {q.text} <span className="text-xs text-[#8a96a8]">(s. {q.page_order})</span></div>
          {q.options?.length ? q.options.map((o, j) => <div key={j} className={`ml-4 text-sm ${teacher && q.correct === j ? "text-[#2f8a4c] font-semibold" : ""}`}>{L[j]}) {o}</div>) : !teacher && <div className="border-b border-dashed h-10 ml-4" />}
          {teacher && <div className="ml-4 text-xs text-[#2f8a4c] mt-1" data-testid="teacher-answer">Cevap: {q.answer} — {q.rationale}</div>}
        </div>))}
    </div>
  );
}
